import React, { useRef, useState } from "react";
import { UploadCloud, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Admin-only file uploader.
 * - kind="image" → returns a public_url that can go straight into <img src>
 * - kind="pdf"   → returns a storage_path that should be stored as publication.pdf_path
 */
export default function FileUploader({ kind = "image", value, onUploaded, label, folder, testidPrefix = "uploader" }) {
    const inputRef = useRef();
    const [busy, setBusy] = useState(false);
    const accept = kind === "image" ? "image/png,image/jpeg,image/webp,image/gif" : "application/pdf";
    const defaultLabel = kind === "image" ? (folder === "gallery" ? "Upload gallery image" : "Upload cover image") : "Upload PDF";

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const folderQuery = folder ? `&folder=${encodeURIComponent(folder)}` : "";
            const { data } = await api.post(`/uploads?kind=${kind}${folderQuery}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onUploaded(data);
            toast.success(`Uploaded ${file.name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };

    return (
        <div>
            {label && <div className="font-mono-cap text-[10px] text-slate-500 mb-2">{label}</div>}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    data-testid={`${testidPrefix}-button`}
                    className="inline-flex items-center gap-2 border border-slate-900/15 bg-white px-4 py-2.5 rounded-sm text-sm hover:border-[#c5a059] transition-colors disabled:opacity-50"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    {busy ? "Uploading…" : label || defaultLabel}
                </button>
                {value && !busy && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5" /> uploaded
                    </span>
                )}
            </div>
            <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onPick} data-testid={`${testidPrefix}-input`} />
        </div>
    );
}
